<template>
  <v-dialog v-model="showDialog" max-width="700px" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ isBulkMode ? `Buy/Rent ${msisdnIds.length} MSISDNs` : 'Buy/Rent MSISDN' }}</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-4">Loading pricing options...</p>
        </div>

        <div v-else>
          <!-- Availability Warning (single mode) -->
          <v-alert v-if="!isBulkMode && !msisdnData.is_available" type="warning" class="mb-4">
            This MSISDN is currently allocated to another service. Purchase may not be possible.
          </v-alert>

          <!-- Bulk Mode Info -->
          <v-alert v-if="isBulkMode" type="info" class="mb-4" variant="tonal">
            <strong>Bulk Purchase:</strong> You are purchasing {{ msisdnIds.length }} MSISDNs.
            The operation will be processed in the background and you will be notified when complete.
          </v-alert>

          <!-- MSISDN Info (single mode) -->
          <v-card v-if="!isBulkMode" variant="outlined" class="mb-4 pa-3">
            <div class="text-subtitle-2 mb-2">MSISDN Details</div>
            <div><strong>Number:</strong> {{ msisdnNumber }}</div>
            <div><strong>Class:</strong> {{ msisdnData.msisdn_class_name || 'N/A' }}</div>
          </v-card>

          <!-- MSISDN List (bulk mode) -->
          <v-card v-if="isBulkMode" variant="outlined" class="mb-4 pa-3">
            <div class="text-subtitle-2 mb-2">Selected MSISDNs ({{ bulkMsisdns.length }})</div>
            <div style="max-height: 150px; overflow-y: auto;">
              <v-chip
                v-for="m in bulkMsisdns"
                :key="m.id"
                size="small"
                class="mr-1 mb-1"
              >
                {{ m.msisdn }}
              </v-chip>
            </div>
          </v-card>

          <!-- Client Selection (for distributors with subclients) -->
          <ClientHierarchySelect
            v-if="hasSubclients"
            v-model="selectedClientId"
            :clients-list="clientsList"
            label="Select Client"
            placeholder="Order for client..."
            auto-select-own
            class="mb-4"
            @change="onClientChange"
          />

          <!-- Step 1: Property Type Selection -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">1. Select Option</div>
            <v-radio-group v-model="propertyType" hide-details>
              <v-radio value="buy" :disabled="!hasBuyOption">
                <template v-slot:label>
                  <span>
                    <strong>Buy</strong> (One-time)
                    <span v-if="hasBuyOption"> - {{ buyPrice }} {{ buyCurrency }}</span>
                    <span v-else class="text-grey"> - Not available</span>
                  </span>
                </template>
              </v-radio>
              <v-radio value="rent" :disabled="!hasRentOptions">
                <template v-slot:label>
                  <span>
                    <strong>Rent</strong> (Recurring)
                    <span v-if="!hasRentOptions" class="text-grey"> - Not available</span>
                  </span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <!-- Step 2: Billing Period (if rent) -->
          <div v-if="propertyType === 'rent'" class="mb-4">
            <div class="text-subtitle-2 mb-2">2. Select Rental Period</div>
            <v-select
              v-model="selectedRentOption"
              :items="rentOptions"
              :item-title="formatRentOption"
              item-value="msisdn_price_id"
              return-object
              label="Rental Period"
              variant="outlined"
              density="compact"
            ></v-select>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Step 3: Physical SIM Option -->
          <div class="mb-4">
            <v-checkbox
              v-model="includePhysicalSim"
              label="Add physical SIM card (IMSI)"
              hide-details
              color="primary"
            ></v-checkbox>
          </div>

          <!-- Step 3a: IMSI Type (if physical SIM) -->
          <div v-if="includePhysicalSim" class="mb-4">
            <v-select
              v-model="selectedImsiType"
              :items="imsiTypes"
              :item-title="formatImsiType"
              item-value="id"
              return-object
              label="Select SIM Type"
              variant="outlined"
              density="compact"
            ></v-select>
          </div>

          <!-- Step 3b: Delivery (if physical SIM) -->
          <div v-if="includePhysicalSim" class="mb-4">
            <DeliveryAddressSelector
              :show-toggle="false"
              :delivery-types="deliveryTypes"
              :client-id="selectedClientId"
              @delivery-changed="onDeliveryChanged"
            />
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Cost Breakdown -->
          <OrderCostBreakdown
            :cost-lines="costBreakdownLines"
            :currency="currency"
            :total-amount="totalCost"
          >
            <template #payment-info>
              <v-alert
                v-if="totalCost === 0"
                type="success"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                <v-icon start size="small">mdi-check-circle</v-icon>
                Cost zero - MSISDN-ul va fi alocat direct fara plata
              </v-alert>
              <v-alert
                v-else-if="targetClientIsPrepaid"
                type="info"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                <v-icon start size="small">mdi-credit-card</v-icon>
                Clientul este prepaid - se va deschide formularul de plata
              </v-alert>
              <v-alert
                v-else
                type="success"
                variant="tonal"
                density="compact"
                class="mt-3"
              >
                <v-icon start size="small">mdi-file-document</v-icon>
                Costul va fi adaugat pe factura lunara
              </v-alert>
            </template>
          </OrderCostBreakdown>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          @click="submitPurchase"
          :loading="submitting"
          :disabled="!canSubmit"
        >
          {{ (targetClientIsPrepaid && totalCost > 0) ? 'Continua la Plata' : 'Cumpara' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar for messages -->
  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
  </v-snackbar>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import store from "@/store";
import { getMsisdnPurchaseOptions, purchaseMsisdn } from "@/composables/useMsisdnPurchaseAPI";
import { getBulkMsisdnPurchaseOptions, bulkPurchaseMsisdns } from "@/composables/useNotificationsAPI";
import { getClientTree } from "@/composables/useGetClientsAPI";
import { getCurrentEuroRonRate } from "@/composables/useEuroRonRateAPI";
import DeliveryAddressSelector from "@/components/DeliveryAddressSelector.vue";
import ClientHierarchySelect from "@/components/order/ClientHierarchySelect.vue";
import OrderCostBreakdown from "@/components/order/OrderCostBreakdown.vue";

export default defineComponent({
  name: 'BuyRentMsisdnDialog',
  components: {
    DeliveryAddressSelector,
    ClientHierarchySelect,
    OrderCostBreakdown
  },
  props: {
    // Single MSISDN mode
    msisdnId: {
      type: [Number, String],
      default: null
    },
    msisdnNumber: {
      type: String,
      default: ''
    },
    // Bulk mode - array of MSISDN IDs
    msisdnIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'purchased'],
  data() {
    return {
      showDialog: true,
      loading: true,
      submitting: false,
      token: '',

      // Data from API
      msisdnData: {},
      pricingOptions: { buy: null, rent: [] },
      imsiTypes: [],
      deliveryTypes: [],

      // Bulk mode data
      bulkMsisdns: [],

      // Client selection
      clientsList: [],
      selectedClientId: null,
      loadingClients: false,
      user: null,

      // Form state
      propertyType: null, // 'buy' or 'rent'
      selectedRentOption: null,
      includePhysicalSim: false,
      selectedImsiType: null,

      // Delivery data from DeliveryAddressSelector component
      deliveryData: {
        deliveryTypeId: null,
        deliveryTypeName: null,
        deliveryCost: 0,
        deliveryDetails: null,
        isValid: true
      },

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Exchange rate EUR/RON
      euroRonRate: 5.0 // Default fallback rate
    }
  },
  computed: {
    isBulkMode() {
      return this.msisdnIds && this.msisdnIds.length > 0;
    },
    msisdnCount() {
      return this.isBulkMode ? this.msisdnIds.length : 1;
    },
    hasBuyOption() {
      return this.pricingOptions.buy != null;
    },
    hasRentOptions() {
      return this.pricingOptions.rent && this.pricingOptions.rent.length > 0;
    },
    buyPrice() {
      return this.pricingOptions.buy?.price || 0;
    },
    buyCurrency() {
      return this.pricingOptions.buy?.currency || 'EUR';
    },
    rentOptions() {
      return this.pricingOptions.rent || [];
    },
    // Display currency is always RON (all amounts converted)
    currency() {
      return 'RON';
    },
    // Original MSISDN currency before conversion
    msisdnOriginalCurrency() {
      if (this.propertyType === 'buy' && this.pricingOptions.buy) {
        return this.pricingOptions.buy.currency || 'EUR';
      }
      if (this.propertyType === 'rent' && this.selectedRentOption) {
        return this.selectedRentOption.currency || 'EUR';
      }
      return 'EUR';
    },
    // MSISDN cost converted to RON
    msisdnCost() {
      let price = 0;
      let currency = 'EUR';
      if (this.propertyType === 'buy' && this.pricingOptions.buy) {
        price = parseFloat(this.pricingOptions.buy.price) || 0;
        currency = this.pricingOptions.buy.currency || 'EUR';
      } else if (this.propertyType === 'rent' && this.selectedRentOption) {
        price = parseFloat(this.selectedRentOption.price) || 0;
        currency = this.selectedRentOption.currency || 'EUR';
      }
      return this.convertToRon(price, currency);
    },
    // IMSI cost converted to RON
    imsiCost() {
      if (this.includePhysicalSim && this.selectedImsiType) {
        const price = parseFloat(this.selectedImsiType.price) || 0;
        const currency = this.selectedImsiType.currency || 'EUR';
        return this.convertToRon(price, currency);
      }
      return 0;
    },
    // Delivery cost (already in RON typically)
    deliveryCost() {
      if (this.includePhysicalSim) {
        const cost = this.deliveryData.deliveryCost || 0;
        const currency = this.deliveryData.deliveryCurrency || 'RON';
        return this.convertToRon(cost, currency);
      }
      return 0;
    },
    totalCost() {
      // In bulk mode, multiply MSISDN cost by count
      const msisdnTotal = this.msisdnCost * this.msisdnCount;
      // IMSI and delivery are per-MSISDN in bulk mode too
      const imsiTotal = this.imsiCost * this.msisdnCount;
      // Delivery is shared (one delivery for all)
      return msisdnTotal + imsiTotal + this.deliveryCost;
    },
    canSubmit() {
      if (!this.propertyType) return false;
      if (this.propertyType === 'rent' && !this.selectedRentOption) return false;
      if (this.includePhysicalSim) {
        if (!this.selectedImsiType) return false;
        if (!this.deliveryData.deliveryTypeId) return false;
        if (!this.deliveryData.isValid) return false;
      }
      return true;
    },
    hasSubclients() {
      return this.clientsList && this.clientsList.length > 1;
    },
    targetClientIsPrepaid() {
      // Find selected client in clientsList
      if (this.selectedClientId && this.clientsList) {
        const client = this.clientsList.find(c => c.id === this.selectedClientId);
        if (client) {
          return client.is_prepaid === true ||
                 client.client_type === 'prepaid' ||
                 client.payment_type === 'prepaid';
        }
      }
      return false;
    },
    costBreakdownLines() {
      const lines = [];

      // MSISDN line - show original price in label
      let msisdnOrigPrice = 0;
      let msisdnOrigCurrency = 'EUR';
      if (this.propertyType === 'buy' && this.pricingOptions.buy) {
        msisdnOrigPrice = parseFloat(this.pricingOptions.buy.price) || 0;
        msisdnOrigCurrency = this.pricingOptions.buy.currency || 'EUR';
      } else if (this.propertyType === 'rent' && this.selectedRentOption) {
        msisdnOrigPrice = parseFloat(this.selectedRentOption.price) || 0;
        msisdnOrigCurrency = this.selectedRentOption.currency || 'EUR';
      }
      const msisdnLabel = `MSISDN (${this.propertyType || 'N/A'}) - ${msisdnOrigPrice.toFixed(2)} ${msisdnOrigCurrency}${this.isBulkMode ? ` x ${this.msisdnCount}` : ''}`;
      lines.push({
        label: msisdnLabel,
        amount: this.msisdnCost * this.msisdnCount
      });

      // Physical SIM line (if included) - show original price in label
      if (this.includePhysicalSim && this.selectedImsiType) {
        const imsiOrigPrice = parseFloat(this.selectedImsiType.price) || 0;
        const imsiOrigCurrency = this.selectedImsiType.currency || 'EUR';
        const simLabel = `Physical SIM - ${imsiOrigPrice.toFixed(2)} ${imsiOrigCurrency}${this.isBulkMode ? ` x ${this.msisdnCount}` : ''}`;
        lines.push({
          label: simLabel,
          amount: this.imsiCost * this.msisdnCount
        });

        // Delivery line - show original price in label
        const deliveryOrigCost = parseFloat(this.deliveryData.deliveryCost) || 0;
        const deliveryOrigCurrency = this.deliveryData.deliveryCurrency || 'RON';
        lines.push({
          label: `Livrare - ${deliveryOrigCost.toFixed(2)} ${deliveryOrigCurrency}`,
          amount: this.deliveryCost
        });
      }
      return lines;
    }
  },
  methods: {
    async loadPurchaseOptions() {
      this.loading = true;
      try {
        let response;
        if (this.isBulkMode) {
          // Bulk mode - load options for multiple MSISDNs
          response = await getBulkMsisdnPurchaseOptions(this.token, this.msisdnIds);
          if (!response.error) {
            this.bulkMsisdns = response.msisdns || [];
            this.pricingOptions = response.pricing_options || { buy: null, rent: [] };
            this.imsiTypes = response.imsi_types || [];
            this.deliveryTypes = response.delivery_types || [];
          }
        } else {
          // Single mode
          response = await getMsisdnPurchaseOptions(this.token, this.msisdnId);
          if (!response.error) {
            this.msisdnData = response.msisdn || {};
            this.pricingOptions = response.pricing_options || { buy: null, rent: [] };
            this.imsiTypes = response.imsi_types || [];
            this.deliveryTypes = response.delivery_types || [];
          }
        }

        if (!response.error) {
          log("Loaded purchase options:");
          log(response);

          // Auto-select first available option
          if (this.hasBuyOption) {
            this.propertyType = 'buy';
          } else if (this.hasRentOptions) {
            this.propertyType = 'rent';
            this.selectedRentOption = this.rentOptions[0];
          }

          // Auto-select first IMSI type
          if (this.imsiTypes.length > 0) {
            this.selectedImsiType = this.imsiTypes[0];
          }
        } else {
          this.showSnackbarMessage(response.message || 'Failed to load pricing', 'error');
        }
      } catch (err) {
        log("Error loading purchase options:");
        log(err);
        this.showSnackbarMessage('Error loading pricing options', 'error');
      } finally {
        this.loading = false;
      }
    },

    async submitPurchase() {
      if (!this.canSubmit) return;

      this.submitting = true;
      try {
        // Determine msisdn_price_id based on selection
        let msisdnPriceId;
        if (this.propertyType === 'buy') {
          msisdnPriceId = this.pricingOptions.buy.msisdn_price_id;
        } else {
          msisdnPriceId = this.selectedRentOption.msisdn_price_id;
        }

        if (this.isBulkMode) {
          // Bulk mode - use async API
          const data = {
            msisdn_ids: this.msisdnIds,
            client_id: this.selectedClientId,
            property_type: this.propertyType,
            msisdn_price_id: msisdnPriceId,
            billing_period_id: this.propertyType === 'rent' ? this.selectedRentOption.billing_period_id : null,
            include_physical_sim: this.includePhysicalSim,
            imsi_type_id: this.includePhysicalSim ? this.selectedImsiType?.id : null,
            delivery_type_id: this.includePhysicalSim ? this.deliveryData.deliveryTypeId : null,
            delivery_details: this.includePhysicalSim ? this.deliveryData.deliveryDetails : null,
            euro_ron_rate: this.euroRonRate,
            skip_payment: this.totalCost === 0
          };

          log("Submitting bulk purchase:");
          log(data);

          const response = await bulkPurchaseMsisdns(this.token, data);

          if (!response.error) {
            this.showSnackbarMessage(response.message || 'Bulk purchase started. You will be notified when complete.', 'success');

            // Emit purchased event
            this.$emit('purchased', { ...response, bulk: true });

            // Close dialog after short delay
            setTimeout(() => {
              this.closeDialog();
            }, 2000);
          } else {
            this.showSnackbarMessage(response.message || 'Failed to start bulk purchase', 'error');
          }
        } else {
          // Single mode - existing logic
          const data = {
            msisdn_id: this.msisdnId,
            client_id: this.selectedClientId,
            property_type: this.propertyType,
            msisdn_price_id: msisdnPriceId,
            billing_period_id: this.propertyType === 'rent' ? this.selectedRentOption.billing_period_id : null,
            include_physical_sim: this.includePhysicalSim,
            imsi_type_id: this.includePhysicalSim ? this.selectedImsiType?.id : null,
            delivery_type_id: this.includePhysicalSim ? this.deliveryData.deliveryTypeId : null,
            delivery_details: this.includePhysicalSim ? this.deliveryData.deliveryDetails : null,
            euro_ron_rate: this.euroRonRate,
            skip_payment: this.totalCost === 0
          };

          log("Submitting purchase:");
          log(data);

          const response = await purchaseMsisdn(this.token, data);

          if (!response.error) {
            this.showSnackbarMessage(response.message || 'Order created successfully!', 'success');

            // Emit purchased event with response data
            this.$emit('purchased', response);

            // Open payment URL if available
            if (response.bt_form_url) {
              window.open(response.bt_form_url, '_blank');
            }

            // Close dialog after short delay
            setTimeout(() => {
              this.closeDialog();
            }, 1500);
          } else {
            this.showSnackbarMessage(response.message || 'Failed to create order', 'error');
          }
        }
      } catch (err) {
        log("Error submitting purchase:");
        log(err);
        this.showSnackbarMessage('Error creating order', 'error');
      } finally {
        this.submitting = false;
      }
    },

    formatRentOption(item) {
      if (!item) return '';
      return `${item.billing_period_name || 'Period'} - ${item.price} ${item.currency || 'EUR'}`;
    },

    formatImsiType(item) {
      if (!item) return '';
      return `${item.name} - ${item.price} ${item.currency || 'EUR'}`;
    },

    onDeliveryChanged(data) {
      log("Delivery changed:");
      log(data);
      this.deliveryData = {
        deliveryTypeId: data.deliveryTypeId,
        deliveryTypeName: data.deliveryTypeName,
        deliveryCost: data.deliveryCost || 0,
        deliveryCurrency: data.deliveryCurrency || 'EUR',
        deliveryDetails: data.deliveryDetails,
        isValid: data.isValid
      };
    },

    showSnackbarMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.$emit('close');
    },

    onClientChange(data) {
      log("Client changed:", data);
      this.selectedClientId = data.clientId;
    },

    // Build flat list from client tree with indentation
    buildClientsList(clientsTree) {
      const list = [];
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(client => {
          const indent = level > 0 ? '—'.repeat(level) + ' ' : '';
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level,
            is_prepaid: client.is_prepaid,
            client_type: client.client_type,
            payment_type: client.payment_type
          });
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1);
          }
        });
      };
      addClientsRecursively(clientsTree);
      return list;
    },

    // Load current EUR/RON exchange rate
    async loadExchangeRate() {
      try {
        const response = await getCurrentEuroRonRate(this.token);
        if (!response.error && response.rate) {
          this.euroRonRate = parseFloat(response.rate.euro_ron_rate) || 5.0;
          log("Loaded EUR/RON rate: " + this.euroRonRate);
        }
      } catch (err) {
        log("Error loading exchange rate, using default:");
        log(err);
      }
    },

    // Convert amount from EUR to RON (rounded to 2 decimal places)
    convertToRon(amount, currency) {
      if (!amount) return 0;
      const numAmount = parseFloat(amount) || 0;
      if (currency === 'RON' || currency === 'Lei') {
        return Math.round(numAmount * 100) / 100;
      }
      // Assume EUR if not RON
      const converted = numAmount * this.euroRonRate;
      return Math.round(converted * 100) / 100;
    },

    // Load clients for autocomplete (hierarchical tree)
    async loadClients() {
      try {
        this.loadingClients = true;
        this.user = store.getters.getUser;
        if (!this.user || !this.user.client_id) {
          log("No user or client_id found");
          return;
        }
        const response = await getClientTree(this.token, this.user.client_id);
        if (!response.error && response.clients_list) {
          this.clientsList = this.buildClientsList(response.clients_list);
          // Pre-select user's own client
          if (this.clientsList.length > 0 && !this.selectedClientId) {
            this.selectedClientId = this.user.client_id;
          }
        }
      } catch (error) {
        log("Error loading clients:");
        log(error);
      } finally {
        this.loadingClients = false;
      }
    }
  },
  mounted() {
    this.token = store.getters.getToken;
    this.loadExchangeRate();
    this.loadPurchaseOptions();
    this.loadClients();
  }
});
</script>

<style scoped>
</style>
